/**
 * Cliente de `/api/me` — a sessão do PRÓPRIO usuário (Story 10.1 + 15.2).
 *
 * Este ficheiro nasceu na 15.2 e recolheu o que já existia: `fetchMe` e
 * `patchMe` viviam dentro de `hooks/use-auth.ts`. Passaram para cá porque a
 * foto acrescenta três chamadas ao MESMO recurso, e deixar metade das
 * chamadas de `/api/me` num hook e a outra metade num cliente daria duas
 * moradas para uma coisa só. O hook fica com o que é dele — as queries, as
 * mutations e a semeadura da cache; aqui fica a rede.
 *
 * Os tipos da sessão (`AuthUser`, `MeResponse`) também mudaram de casa pela
 * mesma razão: são o contrato do fio, e o contrato do fio vive com quem o
 * fala. O `hooks/use-auth.ts` reexporta-os, então nenhum call site mudou.
 *
 * `ApiError` é a MESMA classe de `lib/api/users.ts` — reexportada aqui como o
 * `lib/api/invitations.ts` já fazia. Duas classes com o mesmo nome fariam o
 * `instanceof` das telas mentir consoante o módulo de onde o erro veio.
 */
import type { PortalViews } from '@caliber/shared'
import { ME_URL } from '@/lib/api'
import { readProblemMessage } from '@/lib/api/problem'
import { ApiError } from '@/lib/api/users'

export { ApiError }

/**
 * Application role — exposto por `GET /api/me` desde a Story 10.1 (lido da DB).
 *
 * `'customer'` entra na F1 do portal do cliente (spec de 2026-08-05, §3.2):
 * `users.role` é `TEXT NOT NULL` **sem `CHECK`**, então o papel novo é uma
 * coluna que já aceita o valor — o que muda é quem o escreve e quem o lê.
 *
 * A união vive AQUI, e não em `lib/api/users.ts` (que tem a sua, do contrato de
 * administração de usuários): esta é a da SESSÃO, e é a que as guardas de rota
 * comparam. Alargar a de lá arrastaria o seletor de papel do convite e a
 * listagem de usuários, que são superfícies de outra fase.
 */
export type UserRole = 'admin' | 'recruiter' | 'customer'

export interface AuthUser {
  id: string
  email: string
  name: string
  image?: string | null
  /**
   * Access role (Story 10.1). `/api/me` lê-o da linha de `users`, então está
   * sempre presente numa sessão real. A UI usa-o para esconder o que não
   * interessa; o `requireAdmin` do servidor é a guarda a sério.
   */
  role: UserRole
  /**
   * Story 15.2 — `true` quando existe binário de avatar em R2 para este
   * usuário. É este campo (e NÃO `image`) que decide se se pede a foto:
   * `image` continua a ser a coluna do Better Auth, que nada escreve.
   */
  hasAvatar: boolean
  /**
   * ISO da última escrita da foto, ou `null` quando não há foto. Alimenta o
   * cache-buster de `meAvatarUrl` — sem ele, trocar a foto deixaria o browser
   * a servir a anterior até a página ser recarregada com força.
   */
  avatarUpdatedAt: string | null
  /**
   * As vistas do portal que a pessoa da Genesis pode escolher (issue #584): o perfil
   * de cliente e os clientes de que é gestora de conta (Story 35.9). `null` para o
   * usuário do cliente. Opcional porque as suítes encenam o usuário à mão; ausente é
   * "sem vistas".
   */
  portalViews?: PortalViews | null
  /**
   * Por onde a pessoa entrou (Story 35.3, Epic 35 §8): `genesis` pelo `gms-auth`,
   * `senha` pelo better-auth. Decide qual sessão o «Sair» encerra. Opcional pela mesma
   * razão do `portalViews`; ausente é `senha`, o caminho de antes do Epic 35.
   */
  login?: 'genesis' | 'senha'
  /**
   * A conta `customer` ainda sem empresa (Story 35.5, Epic 35 §6): a pessoa da Genesis
   * com o papel de cliente no `gms-auth`, até um admin a ligar a um cliente. O
   * `PortalRoute` mostra «aguardando o vínculo com a empresa» em vez de um portal que
   * só daria 403. Opcional pela razão do `portalViews`; ausente é `false`.
   */
  awaitingCustomer?: boolean
  /**
   * A pessoa abre o Dashboard de gestão de contas (Story 38.8): o admin, quem é gestor
   * da conta de um cliente ativo e quem é Delivery Manager de uma operação. O menu só
   * mostra o item com `true`. Opcional pela razão do `portalViews`; ausente é `false`.
   */
  accountManagement?: boolean
}

export interface MeResponse {
  user: AuthUser | null
}

const AVATAR_URL = `${ME_URL}/avatar`

/**
 * Lê o usuário de uma resposta de escrita.
 *
 * Tolera as duas formas — `{ user }` (a do `GET`/`PATCH /api/me`) e o objeto
 * cru — porque o servidor da 15.2 foi escrito em paralelo com esta tela e a
 * única frase do contrato era "devolve o utilizador atualizado". A tolerância
 * é deliberada e estreita: só aceita o objeto cru quando ele tem `id`, para
 * um envelope diferente falhar em vez de passar meio vazio.
 */
async function readUser(res: Response): Promise<AuthUser> {
  const data = (await res.json()) as { user?: AuthUser } | AuthUser
  if (data && typeof data === 'object' && 'user' in data && data.user) return data.user
  if (data && typeof data === 'object' && 'id' in data) return data as AuthUser
  throw new Error('Resposta inesperada do servidor')
}

/**
 * `GET /api/me` — a sessão corrente.
 * Devolve `{ user: null }` em 401; lança nos outros erros.
 */
export async function fetchMe(): Promise<MeResponse> {
  const res = await fetch(ME_URL, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (res.status === 401) return { user: null }
  if (!res.ok) {
    throw new Error(`Failed to fetch session: ${res.status}`)
  }
  const data = (await res.json()) as Partial<MeResponse>
  return { user: data.user ?? null }
}

/**
 * `PATCH /api/me` com um nome novo (Story 10.1). O schema do servidor é
 * `.strict()` e só aceita `name`, então este cliente envia EXATAMENTE
 * `{ name }` — qualquer outro campo daria 400. Devolve o usuário atualizado.
 */
export async function patchMe(name: string): Promise<AuthUser> {
  const res = await fetch(ME_URL, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ name }),
  })
  if (!res.ok) {
    throw new Error('Não foi possível salvar o perfil')
  }
  return readUser(res)
}

// ---------------------------------------------------------------------------
// Foto de perfil (Story 15.2)
// ---------------------------------------------------------------------------

/**
 * URL do binário da foto — usado DIRETO no `src` de um `<img>`.
 *
 * `GET /api/me/avatar` devolve os bytes servidos pelo próprio Worker, com o
 * cookie de sessão, e não uma URL pré-assinada (Decisão 3 do épico: um
 * presign expira em 15 minutos e uma aba aberta há meia hora mostraria um
 * avatar partido).
 *
 * O endereço é ESTÁVEL, e é justamente por isso que leva `?v=`: sem o
 * cache-buster, trocar a foto deixaria o browser a servir a anterior da
 * cache. Mesmo padrão do `customerLogoUrl` — `cacheKey` é o `avatarUpdatedAt`.
 */
export function meAvatarUrl(cacheKey?: string | null): string {
  return cacheKey ? `${AVATAR_URL}?v=${encodeURIComponent(cacheKey)}` : AVATAR_URL
}

/**
 * Ordem dos campos do problem+json para os erros do avatar: **`title` primeiro**.
 *
 * Verificado contra `apps/api/src/routes/me.ts`, e é a INVERSÃO da convenção
 * dominante desta API (frase em `detail`, constante inglesa em `title`): as
 * respostas do avatar põem a frase em `title` — "Arquivo muito grande. Tamanho
 * máximo: 2 MB." — e um CÓDIGO em `detail` (`file_too_large`,
 * `unsupported_type`, `missing_file`).
 *
 * Com a ordem por omissão (`['detail', …]`), o 413 mostraria literalmente
 * `file_too_large` ao usuário. É a mesma armadilha que o `problem.ts` já
 * documenta para os endpoints de documento de vaga.
 */
const AVATAR_PROBLEM_FIELDS = ['title', 'detail'] as const

/** Mensagem por omissão quando o servidor não manda uma frase própria. */
function avatarFallback(status: number): string {
  if (status === 413) return 'Imagem muito grande. Tamanho máximo: 2 MB.'
  if (status === 415) return 'Formato de imagem não suportado. Use PNG, JPEG ou WebP.'
  if (status === 400) return 'Nenhuma imagem foi enviada.'
  return `Não foi possível salvar a foto (${status})`
}

/**
 * `POST /api/me/avatar` — multipart, campo `file`.
 *
 * O `Content-Type` NÃO se define à mão: o browser é que escreve o boundary do
 * multipart, e um cabeçalho escrito à mão fica sem ele.
 *
 * O que sobe é o recorte já feito no browser (512×512, WebP ou JPEG), não o
 * ficheiro original — daí o `Blob` + `filename` em vez de um `File`.
 * 415 = tipo não suportado · 413 = passou dos 2 MB · 400 = veio sem ficheiro.
 * A frase que a tela mostra é a do servidor, quando ele a manda.
 */
export async function uploadMyAvatar(blob: Blob, filename: string): Promise<AuthUser> {
  const form = new FormData()
  form.append('file', blob, filename)
  const res = await fetch(AVATAR_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: form,
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readProblemMessage(res, avatarFallback(res.status), AVATAR_PROBLEM_FIELDS),
    )
  }
  return readUser(res)
}

/**
 * `DELETE /api/me/avatar` — remove a foto.
 *
 * Devolve `null` quando o servidor responde sem corpo (204, que é a resposta
 * das outras remoções desta API). Não é um caso de erro: quem chama sabe
 * derivar o estado novo — a foto deixou de existir — a partir do usuário que
 * já tem em mão. Ler o corpo às cegas rebentaria num 204.
 */
export async function deleteMyAvatar(): Promise<AuthUser | null> {
  const res = await fetch(AVATAR_URL, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readProblemMessage(res, 'Não foi possível remover a foto', AVATAR_PROBLEM_FIELDS),
    )
  }
  if (res.status === 204) return null
  try {
    return await readUser(res)
  } catch {
    return null
  }
}
